"use client";

import React, { useMemo } from 'react';
import { Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';

// Constants
const GlbPlayerTeam = {
  HOME: { name: "HOME", value: 1 },
  VISITOR: { name: "VISITOR", value: 2 },
  UNDEFINED: { name: "UNDEFINED", value: 0 }
};

interface LiveScoreboardProps {
  gameState: any; // GameStateSnapshotv2.state
  playersData: any; // gamePlayersData
  settingsData: any; // gameSettingsData
  sport: string;
}

export function LiveScoreboard({ gameState, playersData, settingsData, sport }: LiveScoreboardProps) {
  if (!gameState) return null;

  const currentSet = gameState.currentSet || 1;
  const matchFinished = gameState.matchFinished || false;
  const teamServing = gameState.teamServing || 0;

  // Scores
  const homeSets = gameState.scoreSet?.[GlbPlayerTeam.HOME.name] || 0;
  const visitorSets = gameState.scoreSet?.[GlbPlayerTeam.VISITOR.name] || 0;

  const homePointsRaw = gameState.scorePoint?.[GlbPlayerTeam.HOME.name] || 0;
  const visitorPointsRaw = gameState.scorePoint?.[GlbPlayerTeam.VISITOR.name] || 0;

  // Tie Break logic
  const isTieBreak = gameState.inTieBreak || gameState.inSuperTieBreak;
  
  // Format Points
  let homePointStr = "0";
  let visitorPointStr = "0";

  if (isTieBreak) {
    homePointStr = (gameState.scoreTieBreak?.[GlbPlayerTeam.HOME.name] || 0).toString();
    visitorPointStr = (gameState.scoreTieBreak?.[GlbPlayerTeam.VISITOR.name] || 0).toString();
  } else {
    const fmt = (p: number, opp: number) => {
      if (p === 0) return "0";
      if (p === 15) return "15";
      if (p === 30) return "30";
      if (p === 40) return "40";
      if (p === 50) return opp === 40 ? "Ad" : "40";
      return p.toString();
    };

    const hStr = fmt(homePointsRaw, visitorPointsRaw);
    const vStr = fmt(visitorPointsRaw, homePointsRaw);

    if (hStr === "Ad") {
      homePointStr = "Ad";
      visitorPointStr = "";
    } else if (vStr === "Ad") {
      homePointStr = "";
      visitorPointStr = "Ad";
    } else {
      homePointStr = hStr;
      visitorPointStr = vStr;
    }
  }

  // Set Scores Data
  const setsCount = typeof settingsData?.setCount === 'number' ? settingsData.setCount : 
                   (typeof settingsData?.setCount === 'string' ? parseInt(settingsData.setCount) : 1);
  
  const setsData = useMemo(() => {
    const data = [];
    for (let i = 1; i <= setsCount; i++) {
      const setKey = `set${i}`;
      const scores = gameState.scoreGame?.[setKey];
      const h = scores?.[GlbPlayerTeam.HOME.name] || 0;
      const v = scores?.[GlbPlayerTeam.VISITOR.name] || 0;

      // Only hide if match finished and empty (0-0)
      if (matchFinished && h === 0 && v === 0) continue;
      
      data.push({ setNum: i, h, v });
    }
    return data;
  }, [gameState.scoreGame, setsCount, matchFinished]);

  // Winner logic
  const homeWon = matchFinished && homeSets > visitorSets;
  const visitorWon = matchFinished && visitorSets > homeSets;

  // Serving logic
  const isHomeServing = teamServing === GlbPlayerTeam.HOME.value;
  const isVisitorServing = teamServing === GlbPlayerTeam.VISITOR.value;

  // Settings
  const oneVsOne = settingsData?.oneVsOne === true || settingsData?.oneVsOne === "true";

  // Helpers
  const getPlayerData = (spot: number) => {
    if (!playersData) return null;
    return Object.values(playersData).find((p: any) => {
       const s = typeof p.fldPlayerCourtSpot === 'string' ? parseInt(p.fldPlayerCourtSpot) : p.fldPlayerCourtSpot;
       return s === spot;
    }) as any;
  };

  const PlayerLabel = ({ spot, align = 'left', colorClass }: { spot: number, align?: 'left' | 'right', colorClass: string }) => {
    if (oneVsOne && (spot === 2 || spot === 4)) return <div className="flex-1" />;

    const player = getPlayerData(spot);
    const name = (player?.fldPlayerDisplayName && player.fldPlayerDisplayName !== "MVPlayer" && player.fldPlayerDisplayName !== "Empty slot...") 
                 ? player.fldPlayerDisplayName 
                 : "MVPlayer";

    return (
      <div className={cn("flex-1 flex items-center gap-2", align === 'right' ? "justify-end" : "justify-start")}>
        <span className={cn("text-xl sm:text-2xl font-bold font-sriracha drop-shadow-md truncate max-w-[150px] sm:max-w-[200px]", colorClass)}>
          {name}
        </span>
      </div>
    );
  };

  const ServeIcon = () => (
    <img 
      src={`/drawables/icon_${sport ? sport.toLowerCase() : 'padel'}.png`} 
      alt="Serve" 
      className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
      onError={(e) => { e.currentTarget.style.display = 'none'; }} 
    />
  );

  // Timestamp formatting
  const timeString = useMemo(() => {
      if (!gameState.timeStamp) return "";
      return new Date(gameState.timeStamp).toLocaleTimeString();
  }, [gameState.timeStamp]);

  return (
    <div className="flex flex-col gap-1 p-4 select-none w-full max-w-4xl mx-auto">
      
      {/* --- HOME PLAYERS (TOP) --- */}
      {/* Player 1 (Left) - [LOGO] - Player 2 (Right) */}
      <div className="flex items-end px-2 sm:px-6 pb-2 min-h-[40px]">
         <div className="flex-1 flex justify-start min-w-0">
            <PlayerLabel spot={1} align="left" colorClass="text-[#529AFF]" />
         </div>
         
         <img 
            src="/drawables/mvplayer_logo.png" 
            alt="MVP Logo" 
            className="h-8 sm:h-12 w-auto object-contain mx-2 drop-shadow-lg"
         />

         <div className="flex-1 flex justify-end min-w-0">
            <PlayerLabel spot={2} align="right" colorClass="text-[#529AFF]" />
         </div>
      </div>

      {/* --- HOME ROW --- */}
      <div className="flex items-center h-[100px] bg-[#2A2A2A]/90 rounded-xl p-3 w-full overflow-hidden shadow-xl border border-white/5 relative z-10">
        {/* Sets Won */}
        <div className="w-16 h-full bg-[#529AFF] rounded-lg flex items-center justify-center shrink-0">
          <span className="text-black text-4xl font-bold font-lato">{homeSets}</span>
        </div>

        <div className="w-4"></div>

        {/* Name + Indicators */}
        <div className="flex-1 flex flex-col justify-center min-w-0 mr-3">
          <div className="flex items-center">
            <span className="text-white text-3xl sm:text-4xl font-bold truncate font-sans tracking-tight mr-3">
              HOME
            </span>
            {homeWon && <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-[#FFD700] shrink-0 animate-bounce" />}
            {!matchFinished && isHomeServing && <ServeIcon />}
          </div>
        </div>

        {/* Set Scores */}
        <div className="flex items-center h-full mr-2">
            {setsData.map((set, idx) => (
                <div key={idx} className="w-[50px] sm:w-[70px] h-full flex items-center justify-center">
                    <span className={cn(
                        "text-white text-3xl sm:text-4xl font-lato",
                        (matchFinished && set.h > set.v) ? "font-bold text-[#529AFF]" : "font-normal opacity-80"
                    )}>
                        {set.h}
                    </span>
                </div>
            ))}
        </div>

        {/* Points */}
        {!matchFinished && (
            <div className="w-[80px] sm:w-[100px] h-full bg-[#404040] rounded-lg flex items-center justify-center shrink-0 ml-1 border border-white/10">
                <span className="text-white text-4xl sm:text-5xl font-bold font-lato tracking-tighter">
                    {homePointStr}
                </span>
            </div>
        )}
      </div>

      {/* --- VISITOR ROW --- */}
      <div className="flex items-center h-[100px] bg-[#2A2A2A]/90 rounded-xl p-3 w-full overflow-hidden shadow-xl border border-white/5 mt-2 relative z-10">
        {/* Sets Won */}
        <div className="w-16 h-full bg-[#ef4444] rounded-lg flex items-center justify-center shrink-0">
          <span className="text-black text-4xl font-bold font-lato">{visitorSets}</span>
        </div>

        <div className="w-4"></div>

        {/* Name + Indicators */}
        <div className="flex-1 flex flex-col justify-center min-w-0 mr-3">
          <div className="flex items-center">
            <span className="text-white text-3xl sm:text-4xl font-bold truncate font-sans tracking-tight mr-3">
              VISITOR
            </span>
            {visitorWon && <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-[#FFD700] shrink-0 animate-bounce" />}
            {!matchFinished && isVisitorServing && <ServeIcon />}
          </div>
        </div>

        {/* Set Scores */}
        <div className="flex items-center h-full mr-2">
            {setsData.map((set, idx) => (
                <div key={idx} className="w-[50px] sm:w-[70px] h-full flex items-center justify-center">
                    <span className={cn(
                        "text-white text-3xl sm:text-4xl font-lato",
                        (matchFinished && set.v > set.h) ? "font-bold text-[#ef4444]" : "font-normal opacity-80"
                    )}>
                        {set.v}
                    </span>
                </div>
            ))}
        </div>

        {/* Points */}
        {!matchFinished && (
            <div className="w-[80px] sm:w-[100px] h-full bg-[#404040] rounded-lg flex items-center justify-center shrink-0 ml-1 border border-white/10">
                <span className="text-white text-4xl sm:text-5xl font-bold font-lato tracking-tighter">
                    {visitorPointStr}
                </span>
            </div>
        )}
      </div>

      {/* --- VISITOR PLAYERS (BOTTOM) --- */}
      {/* Player 4 (Left) - [Timestamp] - Player 3 (Right) */}
      <div className="flex items-start px-2 sm:px-6 pt-2 min-h-[40px]">
         <div className="flex-1 flex justify-start min-w-0">
            <PlayerLabel spot={4} align="left" colorClass="text-[#ef4444]" />
         </div>
         
         {/* Timestamp in the middle */}
         {timeString && (
            <div className="text-[#8E9285] text-xs font-lato pt-1 opacity-70 mx-2">
                {timeString}
            </div>
         )}

         <div className="flex-1 flex justify-end min-w-0">
            <PlayerLabel spot={3} align="right" colorClass="text-[#ef4444]" />
         </div>
      </div>

    </div>
  );
}
